import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Share2, X, Copy, Check } from "lucide-react";
import { api } from "@/lib/api-client";
import { Button } from "@/components/ui/Button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/Dialog";
import { DatePicker } from "@/components/ui/DatePicker";
import { InputGroup } from "@/components/ui/Input";
import { toast } from "@/components/ui/Toast";
import { cn } from "@/lib/utils";
import { useTranslation } from "@/hooks/useTranslation";
import type { Share } from "@/types";

// ─────────────────────────────────────────────
// CreateShareModal
//
// Modal to create a new share link for an assessment.
// Fields: recipient email (single, required), allowed domains (optional
// domain codes), expiry date (required). POST /shares.
// On success, shows the one-time share link — the raw token is only ever
// returned once, so it must be copied here or it's gone.
// Design: §8.5 Sharing UI
// ─────────────────────────────────────────────

interface CreateShareModalProps {
  assessmentId: string;
  open: boolean;
  onClose: () => void;
}

interface CreateShareResponse {
  data: Share & { token: string };
}

function DomainTagInput({
  values,
  onChange,
}: {
  values: string[];
  onChange: (values: string[]) => void;
}) {
  const { t } = useTranslation();
  const [input, setInput] = useState("");

  function add() {
    // Domain codes are schema-defined (e.g. "business-impact"), not hostnames —
    // just trim/lowercase and dedupe, no hostname-format validation.
    const trimmed = input.trim().toLowerCase();
    if (!trimmed) return;
    if (!values.includes(trimmed)) {
      onChange([...values, trimmed]);
    }
    setInput("");
  }

  function remove(v: string) {
    onChange(values.filter((x) => x !== v));
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      add();
    }
    if (e.key === "Backspace" && !input && values.length > 0) {
      onChange(values.slice(0, -1));
    }
  }

  return (
    <div>
      <p className="mb-1.5 text-[14px] font-medium text-text-secondary">{t("sharing.allowedDomains")}</p>
      <div
        className={cn(
          "flex min-h-[38px] flex-wrap gap-1.5 rounded-control border border-border-default bg-bg-input px-3 py-1.5",
          "focus-within:border-accent focus-within:ring-1 focus-within:ring-accent"
        )}
      >
        {values.map((v) => (
          <span
            key={v}
            className="flex items-center gap-1 rounded-badge bg-accent-bg px-2 py-0.5 text-[12px] font-medium text-accent-text"
          >
            {v}
            <button
              type="button"
              onClick={() => remove(v)}
              className="text-accent-text/70 hover:text-accent-text"
            >
              <X className="h-3 w-3" />
            </button>
          </span>
        ))}
        <input
          type="text"
          placeholder={values.length === 0 ? t("sharing.domainPlaceholder") : ""}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          onBlur={add}
          className="min-w-[120px] flex-1 bg-transparent text-[14px] text-text-primary placeholder:text-text-muted focus:outline-none"
        />
      </div>
      <p className="mt-1 text-caption text-text-muted">
        {t("sharing.domainHint")}
      </p>
    </div>
  );
}

function isValidEmail(v: string) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);
}

export function CreateShareModal({ assessmentId, open, onClose }: CreateShareModalProps) {
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState<string | undefined>(undefined);
  const [allowedDomains, setAllowedDomains] = useState<string[]>([]);
  const [expiresAt, setExpiresAt] = useState<Date | undefined>(undefined);
  const [shareLink, setShareLink] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const mutation = useMutation({
    mutationFn: (body: {
      assessmentId: string;
      externalEmail: string;
      allowedDomains: string[];
      expiresAt: string;
    }) => api.post<CreateShareResponse>("/shares", body),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ["assessment-shares", assessmentId] });
      toast.success(t("sharing.shareLinkCreated"));
      setShareLink(`${window.location.origin}/external/${res.data.token}`);
    },
    onError: (err: Error) => toast.error(t("sharing.createFailed"), err.message),
  });

  function resetState() {
    setEmail("");
    setEmailError(undefined);
    setAllowedDomains([]);
    setExpiresAt(undefined);
    setShareLink(null);
    setCopied(false);
  }

  function handleClose() {
    resetState();
    onClose();
  }

  function handleSubmit() {
    const trimmed = email.trim();
    if (!trimmed || !isValidEmail(trimmed)) {
      setEmailError(t("sharing.invalidEmail"));
      return;
    }
    if (!expiresAt) {
      toast.error(t("sharing.expiryRequired"));
      return;
    }
    mutation.mutate({
      assessmentId,
      externalEmail: trimmed,
      allowedDomains,
      expiresAt: expiresAt.toISOString(),
    });
  }

  async function handleCopy() {
    if (!shareLink) return;
    try {
      await navigator.clipboard.writeText(shareLink);
      setCopied(true);
      toast.success(t("sharing.linkCopied"));
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error(t("sharing.copyFailed"), t("sharing.copyFailedDesc"));
    }
  }

  return (
    <Dialog open={open} onOpenChange={(v) => !v && handleClose()}>
      <DialogContent>
        {shareLink ? (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Share2 className="h-4 w-4 text-accent" />
                {t("sharing.shareLinkCreated")}
              </DialogTitle>
            </DialogHeader>

            <div className="flex flex-col gap-3 py-2">
              <p className="text-body text-text-secondary">
                {t("sharing.sendLinkPrefix")}{" "}
                <span className="font-medium text-text-primary">{email}</span>
                {t("sharing.sendLinkSuffix")}
              </p>
              <div className="flex items-center gap-2 rounded-control border border-border-default bg-bg-input px-3 py-2">
                <span className="flex-1 truncate font-mono text-[13px] text-text-primary">
                  {shareLink}
                </span>
                <Button type="button" variant="ghost" size="icon-sm" onClick={handleCopy}>
                  {copied ? (
                    <Check className="h-4 w-4 text-success" />
                  ) : (
                    <Copy className="h-4 w-4" />
                  )}
                </Button>
              </div>
            </div>

            <DialogFooter>
              <Button onClick={handleClose}>{t("common.done")}</Button>
            </DialogFooter>
          </>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Share2 className="h-4 w-4 text-accent" />
                {t("sharing.shareAssessment")}
              </DialogTitle>
            </DialogHeader>

            <div className="flex flex-col gap-4 py-2">
              <InputGroup
                label={t("sharing.recipientEmail")}
                type="email"
                placeholder={t("sharing.recipientEmailPlaceholder")}
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  setEmailError(undefined);
                }}
                errorText={emailError}
                required
              />
              <DomainTagInput values={allowedDomains} onChange={setAllowedDomains} />
              <DatePicker
                label={t("sharing.expiryDate")}
                value={expiresAt}
                onChange={setExpiresAt}
                fromDate={new Date()}
                placeholder={t("sharing.selectExpiryDate")}
                required
              />
            </div>

            <DialogFooter>
              <Button variant="ghost" onClick={handleClose}>
                {t("common.cancel")}
              </Button>
              <Button
                onClick={handleSubmit}
                isLoading={mutation.isPending}
                disabled={!email.trim() || !expiresAt}
              >
                <Share2 className="mr-2 h-4 w-4" />
                {t("sharing.createShare")}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
